import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ImportantMessageInput } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetImportantMessagesQuery = (activeNow?: boolean) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['important-messages'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/important-messages', {
        params: {
          query: {
            activeNow
          }
        }
      })
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useCreateImportantMessageMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (message: ImportantMessageInput) => {
      const { data, error } = await authFetch().POST('/important-messages', {
        body: message
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['important-messages']
      })
    }
  })
}

interface UpdateImportantMessageMutationVariables {
  messageId: number
  etag: string
  body: ImportantMessageInput
}

export const useUpdateImportantMessageMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      messageId,
      etag,
      body
    }: UpdateImportantMessageMutationVariables) => {
      const { data, error } = await authFetch().PUT(
        '/important-messages/{messageId}',
        {
          params: {
            header: {
              'If-Match': etag
            },
            path: { messageId }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['important-messages']
      })
    }
  })
}

interface DeleteImportantMessageMutationVariables {
  messageId: number
  etag: string
}

export const useDeleteImportantMessageMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      messageId,
      etag
    }: DeleteImportantMessageMutationVariables) =>
      await authFetch().DELETE('/important-messages/{messageId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { messageId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['important-messages']
      })
    }
  })
}
